/* eslint-disable @typescript-eslint/naming-convention */
import {
  completeUpload,
  CompleteUpload,
  CompleteUploadResponse,
  createDataset,
  CreateDataset,
  CreateDatasetResponse,
  createImages,
  CreateImage,
  CreateImagesResponse,
  getDataset,
  GetDatasetResponse,
  getImage,
  GetImageResponse,
  getImageUrls,
  GetImageUrlsResponse,
  GetUrlsRequest,
  ingestDataset,
  IngestDatasetResponse,
  ingestImage,
  IngestImageResponse,
  JobStatus,
  JobType,
  listDatasets,
  listImages,
  ListImagesResponse,
  listJobs,
  ListJobsResponse,
  RotationOrder,
  type Axis,
  type AxisOrientation,
  type RotationMode,
} from '../generated/index';
import type { Client } from '../generated/client/types.gen';
import {
  QueryClient,
  useQuery,
  type UseQueryResult,
} from '@tanstack/solid-query';
import { returnOrThrow } from '@trimble/http-utils';
import type { Accessor } from 'solid-js';
import {
  getDefaultImagingHeaders,
  ImagingDataHeaders,
} from './utils/imaging-header';
import {
  CoordinateSystemConfig,
  DATASETS_LIST_PAGE_SIZE,
  IMAGES_PAGE_SIZE,
} from './imaging-constants';

export type ListImagesQueryKey = [
  'images',
  string,
  string,
  boolean | undefined,
  boolean | undefined,
  RotationMode | undefined,
  Axis | undefined,
  Axis | undefined,
  AxisOrientation | undefined,
  number,
  number,
];

export function buildListImagesQueryKey(
  tenantTrn: string,
  datasetId: string,
  getImageUrls?: boolean,
  getTilesetUrls?: boolean,
  coordinateSystem?: CoordinateSystemConfig,
  pageIndex?: number,
  pageSize?: number
): ListImagesQueryKey {
  return [
    'images',
    tenantTrn,
    datasetId,
    getImageUrls,
    getTilesetUrls,
    coordinateSystem?.rotationMode,
    coordinateSystem?.targetUpAxis,
    coordinateSystem?.targetForwardAxis,
    coordinateSystem?.targetHandedness,
    pageIndex ?? 0,
    pageSize ?? IMAGES_PAGE_SIZE,
  ];
}

export class ImagingApiService {
  private readonly headers: ImagingDataHeaders;

  constructor(
    private readonly client: Client,
    clientName: string,
    clientVersion: string,
    private readonly queryClient: QueryClient
  ) {
    this.headers = getDefaultImagingHeaders(clientName, clientVersion);
  }

  private async fetchDataset(
    tenantTrn: string,
    datasetId: string
  ): Promise<GetDatasetResponse> {
    const response = await getDataset({
      headers: this.headers,
      path: {
        tenantTrn,
        datasetId,
      },
      client: this.client,
    });
    return returnOrThrow(response, 'Error while getting dataset');
  }

  public getDatasetQuery(
    tenantTrn: string,
    datasetId: string
  ): Promise<GetDatasetResponse> {
    return this.queryClient.fetchQuery({
      queryKey: ['dataset', tenantTrn, datasetId],
      queryFn: () => this.fetchDataset(tenantTrn, datasetId),
    });
  }

  public getDatasetDetailsQuery(
    tenantTrn: Accessor<string | undefined>,
    datasetId: Accessor<string | undefined>
  ): UseQueryResult<GetDatasetResponse, Error> {
    return useQuery(() => {
      const trn = tenantTrn();
      const id = datasetId();
      return {
        queryKey: ['dataset', trn, id],
        enabled: Boolean(trn) && Boolean(id),
        queryFn: () => this.fetchDataset(trn!, id!),
      };
    });
  }

  public listDatasetsQuery(tenantTrn: string): Promise<GetDatasetResponse[]> {
    return this.queryClient.fetchQuery({
      queryKey: ['datasetList', tenantTrn],
      queryFn: async () => {
        const datasets: GetDatasetResponse[] = [];
        let pageIndex = 0;
        let lastPageSize: number;

        do {
          const response = await listDatasets({
            headers: this.headers,
            path: { tenantTrn },
            query: {
              PageIndex: pageIndex,
              PageSize: DATASETS_LIST_PAGE_SIZE,
            },
            client: this.client,
          });
          const page = returnOrThrow(
            response,
            'Error while getting dataset list'
          );
          const items = page.items ?? [];
          datasets.push(...items);
          lastPageSize = items.length;
          pageIndex += 1;
        } while (lastPageSize === DATASETS_LIST_PAGE_SIZE);

        return datasets;
      },
    });
  }

  public invalidateDatasetListQuery(tenantTrn: string): Promise<void> {
    return this.queryClient.invalidateQueries({
      queryKey: ['datasetList', tenantTrn],
    });
  }

  public listImagesQuery(
    tenantTrn: string,
    datasetId: string,
    getImageUrls?: boolean,
    getTilesetUrls?: boolean,
    coordinateSystem?: CoordinateSystemConfig,
    pageIndex?: number,
    pageSize?: number
  ): Promise<ListImagesResponse> {
    const actualPageIndex = pageIndex ?? 0;
    const actualPageSize = pageSize ?? IMAGES_PAGE_SIZE;

    return this.queryClient.fetchQuery({
      queryKey: buildListImagesQueryKey(
        tenantTrn,
        datasetId,
        getImageUrls,
        getTilesetUrls,
        coordinateSystem,
        actualPageIndex,
        actualPageSize
      ),
      queryFn: async () => {
        const response = await listImages({
          headers: this.headers,
          path: {
            tenantTrn,
            datasetId,
          },
          query: {
            PageIndex: actualPageIndex,
            PageSize: actualPageSize,
            GetImageUrls: getImageUrls,
            GetTilesetUrls: getTilesetUrls,
            RotationMode: coordinateSystem?.rotationMode,
            TargetUpAxis: coordinateSystem?.targetUpAxis,
            TargetForwardAxis: coordinateSystem?.targetForwardAxis,
            TargetHandedness: coordinateSystem?.targetHandedness,
            TargetRotationOrder:
              coordinateSystem?.targetRotationOrder ??
              ('Y_P_R' as RotationOrder), // TODO: Target rotation order is not required for calculation, but it is currently mandatory for the API.
          },
          client: this.client,
        });

        return returnOrThrow(response, 'Error while listing images');
      },
    });
  }

  public getImageQuery(
    tenantTrn: string,
    datasetId: string,
    imageId: string,
    coordinateSystem?: CoordinateSystemConfig
  ): Promise<GetImageResponse> {
    return this.queryClient.fetchQuery({
      queryKey: [
        'image',
        tenantTrn,
        datasetId,
        imageId,
        coordinateSystem?.rotationMode,
        coordinateSystem?.targetForwardAxis,
        coordinateSystem?.targetHandedness,
        coordinateSystem?.targetRotationOrder,
        coordinateSystem?.targetUpAxis,
      ],
      queryFn: async () => {
        const response = await getImage({
          headers: this.headers,
          path: {
            tenantTrn,
            datasetId,
            imageId,
          },
          client: this.client,
          query: {
            RotationMode: coordinateSystem?.rotationMode,
            TargetForwardAxis: coordinateSystem?.targetForwardAxis,
            TargetHandedness: coordinateSystem?.targetHandedness,
            TargetRotationOrder: coordinateSystem?.targetRotationOrder,
            TargetUpAxis: coordinateSystem?.targetUpAxis,
          },
        });
        return returnOrThrow(response, 'Error while getting image');
      },
    });
  }

  public async createDataset(
    tenantTrn: string,
    body: CreateDataset
  ): Promise<CreateDatasetResponse> {
    const response = await createDataset({
      headers: this.headers,
      path: { tenantTrn },
      body,
      client: this.client,
    });

    return returnOrThrow(response, 'Error while creating dataset');
  }

  public async createImages(
    tenantTrn: string,
    datasetId: string,
    body: Array<CreateImage>
  ): Promise<CreateImagesResponse> {
    const response = await createImages({
      headers: this.headers,
      path: { tenantTrn, datasetId },
      body,
      client: this.client,
    });

    return returnOrThrow(response, 'Error while creating images');
  }

  public async completeUpload(
    tenantTrn: string,
    datasetId: string,
    imageId: string,
    body?: CompleteUpload
  ): Promise<CompleteUploadResponse> {
    const response = await completeUpload({
      headers: this.headers,
      path: { tenantTrn, datasetId, imageId },
      body: {
        parts: body?.parts ?? [],
      },
      client: this.client,
    });

    return returnOrThrow(response, 'Error while completing upload');
  }

  public async ingestImage(
    tenantTrn: string,
    datasetId: string,
    imageId: string
  ): Promise<IngestImageResponse> {
    const response = await ingestImage({
      headers: this.headers,
      path: { tenantTrn, datasetId, imageId },
      client: this.client,
    });

    return returnOrThrow(response, 'Error while ingesting image');
  }

  public listJobsQuery(
    tenantTrn: string,
    datasetId: string,
    type?: JobType,
    status?: JobStatus,
    pageIndex?: number,
    pageSize?: number
  ): Promise<ListJobsResponse> {
    return this.queryClient.fetchQuery({
      queryKey: [
        'jobs',
        tenantTrn,
        datasetId,
        type,
        status,
        pageIndex,
        pageSize,
      ],
      staleTime: 0,
      queryFn: async () => {
        const response = await listJobs({
          headers: this.headers,
          path: {
            tenantTrn,
            datasetId,
          },
          query: {
            Type: type,
            Status: status,
            PageIndex: pageIndex,
            PageSize: pageSize,
          },
          client: this.client,
        });

        return returnOrThrow(response, 'Error while listing jobs');
      },
    });
  }

  public async triggerDatasetIngestion(
    tenantTrn: string,
    datasetId: string
  ): Promise<IngestDatasetResponse> {
    const response = await ingestDataset({
      headers: this.headers,
      path: { tenantTrn, datasetId },
      client: this.client,
    });

    return returnOrThrow(response, 'Error while triggering dataset ingestion');
  }

  public getTilesetUrlsQuery(
    tenantTrn: string,
    datasetId: string,
    imageIds: string[]
  ): Promise<GetImageUrlsResponse> {
    const sortedIds = [...imageIds].sort();
    return this.queryClient.fetchQuery({
      queryKey: ['tileset-urls', tenantTrn, datasetId, ...sortedIds],
      queryFn: async () => {
        const response = await getImageUrls({
          headers: this.headers,
          path: {
            tenantTrn,
            datasetId,
          },
          client: this.client,
          body: {
            imageIds,
            getTilesetUrls: true,
          } satisfies GetUrlsRequest,
        });
        return returnOrThrow(response, 'Error while getting tileset urls');
      },
    });
  }
}
